// Copyright (c) Ping. All rights reserved.

import type React from 'react';

import {
  type Location,
  type LocationSearchResult,
  ShareLocationOverlay,
  type ShareLocationResult,
} from '@ping/ui';

import type { ChatHostProps } from './types';

/**
 * invite-flow Wave 2 — web orchestrator for the shared
 * `ShareLocationOverlay`.
 *
 * Coordinates (`Location.lat` / `.lng`) live in memory only and are
 * NEVER logged — Hard rule 3. The shared overlay forwards them to
 * `onShare` without persisting or logging.
 *
 * Web supplies `onResolveCurrentLocation` via `navigator.geolocation`
 * (typically wired in `synthesizeShareLocationHost`). Desktop omits
 * the prop until react-native-geolocation-service / CLLocationManager
 * lands — see the desktop host for the follow-up note.
 */
export interface ShareLocationHostProps extends ChatHostProps {
  readonly onShare: (result: ShareLocationResult) => Promise<void> | void;
  readonly onSearch?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<readonly LocationSearchResult[]>;
  readonly onResolveCurrentLocation?: (signal: AbortSignal) => Promise<Location | null>;
  readonly defaultLiveDurationSeconds?: number;
  readonly conversationName?: string | undefined;
  readonly liveMode?: boolean | undefined;
}

export const ShareLocationHost: React.FC<ShareLocationHostProps> = ({
  vm,
  onShare,
  onSearch,
  onResolveCurrentLocation,
  defaultLiveDurationSeconds,
  conversationName,
  liveMode,
}) => (
  <ShareLocationOverlay
    vm={vm}
    onShare={onShare}
    {...(onSearch !== undefined ? { onSearch } : {})}
    {...(onResolveCurrentLocation !== undefined ? { onResolveCurrentLocation } : {})}
    {...(defaultLiveDurationSeconds !== undefined ? { defaultLiveDurationSeconds } : {})}
    {...(conversationName !== undefined ? { conversationName } : {})}
    {...(liveMode !== undefined ? { liveMode } : {})}
  />
);
